import React, { useState } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import CreateWizzardForm from '../../components/project-management/create-new/CreateWizzardForm.js';
import { CABLE_NAMES, CABLE_COLORS } from '../../data/cableProducts.js';
import { API } from '../../config/api';
import {
  findStandardFor,
  describeLaunchLine
} from '../../helpers/standardMatch';

const productNames = CABLE_NAMES;
const colorOptions = CABLE_COLORS;

const isSemifabricat = (name) => name === 'COPPER_ROPE_SOLID' || name === 'COPPER_ROPE_FLEXIBLE';

const createLaunchInstanceId = () =>
  'launch_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);

const createEmptyProduct = () => ({
  launchInstanceId: createLaunchInstanceId(),
  name: '',
  numberOfConductors: '',
  section: '',
  length: '',
  color: '',
  hasMixedConductorSections: false,
  greenYellowSection: ''
});

const CreateNewProduct = () => {
  const [products, setProducts] = useState([createEmptyProduct()]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [standardsData, setStandardsData] = useState(null);

  const clearStandardsData = () => {
    setStandardsData(null);
  };

  const handleChange = (index, field, value) => {
    setProducts(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };

      // auto-fill defaults for semifabricat (COPPER_ROPE_*)
      if (field === 'name' && isSemifabricat(value)) {
        copy[index].numberOfConductors = 1;
        copy[index].color = 'N/A';
        copy[index].hasMixedConductorSections = false;
        copy[index].greenYellowSection = '';
      } else if (field === 'name' && !isSemifabricat(value) && copy[index].color === 'N/A') {
        copy[index].color = '';
      }

      return copy;
    });
    setError('');
  };

  const addProduct = () => {
    setProducts(prev => [...prev, createEmptyProduct()]);
    setError('');
  };

  const removeProduct = index => {
    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  const isFormValid = products.every(p => {
    const semi = isSemifabricat(p.name);
    return (
      p.name.trim() !== '' &&
      p.numberOfConductors !== '' &&
      p.section !== '' &&
      p.length !== '' &&
      (semi || p.color.trim() !== '') &&
      (!p.hasMixedConductorSections || p.greenYellowSection !== '')
    );
  });

  // Standard lookup (colour included) — helpers/standardMatch.js. Launching the
  // same cable in several colours at once used to give every card the first
  // colour returned, because the match ignored the colour the form asked for.

  const createLaunchId = () =>
    'launch_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

  const normalizeConductorIsolationPhase = phase => {
    if (!phase || phase.name !== 'conductor_isolation') return phase;

    if (Array.isArray(phase.conductors)) {
      return {
        ...phase,
        conductors: phase.conductors.map(c => ({
          ...c,
          execution: Array.isArray(c.execution) ? c.execution : []
        }))
      };
    }

    if (phase.wire && phase.isolation) {
      const { wire, isolation, ...rest } = phase;

      return {
        ...rest,
        conductors: [
          {
            wire: { ...wire },
            isolation: { ...isolation },
            execution: []
          }
        ]
      };
    }

    return {
      ...phase,
      conductors: []
    };
  };
  const fetchStandards = async () => {
    if (!isFormValid) {
      setError('Please fill in all required fields before continuing.');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const response = await axios.post(
        `${API.BACKEND}/api/v1/standard/find-many`,
        products
      );

      const standards = response.data.data;

      const expandedStandards = products.map((product, index) => {
        const standard = findStandardFor(standards, product);

        if (!standard) {
          throw new Error(
            `No standard found for ${describeLaunchLine(product)}`
          );
        }

        console.log('standardID ', standard._id);

        return {
          ...structuredClone(standard),
          standardId: standard._id,
          _id: createLaunchId(),
          launchInstanceId: product.launchInstanceId,
          launchIndex: index,
          requestedLength: product.length,
          requestedColor: product.color,
          requestedProductName: product.name,
          hasMixedConductorSections: product.hasMixedConductorSections,
          greenYellowSection: product.greenYellowSection || null,
          length: product.length,
          phases: standard.phases.map((phase, phaseIndex, arr) => {
            const previousPhase =
              phaseIndex > 0 ? arr[phaseIndex - 1].name : null;

            if (phase.name === 'conductor_isolation') {
              const normalizedPhase = normalizeConductorIsolationPhase(
                structuredClone(phase)
              );

              return {
                ...normalizedPhase,
                dependsOn: previousPhase
              };
            }

            return {
              ...structuredClone(phase),
              dependsOn: previousPhase,
              execution: []
            };
          })
        };
      });

      setStandardsData(expandedStandards);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to fetch standards.');
    } finally {
      setLoading(false);
    }
  };

  if (standardsData) {
    return (
      <CreateWizzardForm
        standardData={standardsData}
        setStandardsData={setStandardsData}
        clearStandardsData={clearStandardsData}
      />
    );
  }

  return (
    <div className="mb-4">
      <h1 className="fw-semibold mb-3">Product Launch</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form>
        {products.map((product, index) => (
          <Card key={product.launchInstanceId} className="mb-4 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="fw-semibold">
                {isSemifabricat(product.name)
                  ? `Semifabricat #${index + 1} — ${product.name.replace('COPPER_ROPE_', 'Copper Rope ')}`
                  : `Product #${index + 1}`
                }
              </div>

              {!isSemifabricat(product.name) && (
                <Form.Check
                  type="switch"
                  label="GY different section"
                  checked={product.hasMixedConductorSections}
                  onChange={e =>
                    handleChange(
                      index,
                      'hasMixedConductorSections',
                      e.target.checked
                    )
                  }
                />
              )}
            </Card.Header>

            <Card.Body>
              {(() => {
                const semi = isSemifabricat(product.name);

                return (
                  <>
                    <Row className="mb-3">
                      <Col md={semi ? 4 : 4}>
                        <Form.Label>Name *</Form.Label>
                        <Form.Select
                          value={product.name}
                          onChange={e => handleChange(index, 'name', e.target.value)}
                        >
                          <option value="">Select</option>
                          {productNames.map(n => (
                            <option key={n}>{n}</option>
                          ))}
                        </Form.Select>
                      </Col>

                      {!semi && (
                        <Col md={4}>
                          <Form.Label>Conductors *</Form.Label>
                          <Form.Control
                            type="number"
                            value={product.numberOfConductors}
                            onChange={e =>
                              handleChange(index, 'numberOfConductors', e.target.value)
                            }
                          />
                        </Col>
                      )}

                      <Col md={4}>
                        <Form.Label>Section (mm²) *</Form.Label>
                        <Form.Control
                          type="number"
                          value={product.section}
                          onChange={e =>
                            handleChange(index, 'section', e.target.value)
                          }
                        />
                      </Col>

                      {semi && (
                        <Col md={4}>
                          <Form.Label>Length (m) *</Form.Label>
                          <Form.Control
                            type="number"
                            value={product.length}
                            onChange={e =>
                              handleChange(index, 'length', Number(e.target.value))
                            }
                          />
                        </Col>
                      )}
                    </Row>

                    {!semi && product.hasMixedConductorSections && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label className="text-success">
                            Green-Yellow Section *
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={product.greenYellowSection}
                            onChange={e =>
                              handleChange(
                                index,
                                'greenYellowSection',
                                e.target.value
                              )
                            }
                          />
                        </Col>
                      </Row>
                    )}

                    {!semi && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label>Length (m) *</Form.Label>
                          <Form.Control
                            type="number"
                            value={product.length}
                            onChange={e =>
                              handleChange(index, 'length', Number(e.target.value))
                            }
                          />
                        </Col>

                        <Col md={4}>
                          <Form.Label>Color *</Form.Label>
                          <Form.Select
                            value={product.color}
                            onChange={e => handleChange(index, 'color', e.target.value)}
                          >
                            <option value="">Select</option>
                            {colorOptions.map(c => (
                              <option key={c}>{c}</option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    )}
                  </>
                );
              })()}

              {products.length > 1 && (
                <div className="text-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeProduct(index)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}

        <Row className="mt-4">
          <Col>
            <Button variant="secondary" onClick={addProduct}>
              Add Pair Product
            </Button>
          </Col>
          <Col className="text-end">
            <Button
              variant="primary"
              disabled={!isFormValid || loading}
              onClick={fetchStandards}
            >
              {loading ? <Spinner size="sm" /> : 'Search Standards & Continue'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CreateNewProduct;
